import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './LearningTracks.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const DIFFICULTIES = ['All', 'Beginner', 'Intermediate', 'Advanced'];

const DIFFICULTY_COLORS = {
  Beginner: 'green',
  Intermediate: 'blue',
  Advanced: 'red',
};

function LearningTracks({ factFictionMode }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [view, setView] = useState('browse');
  const [tracks, setTracks] = useState([]);
  const [myTracks, setMyTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [difficultyFilter, setDifficultyFilter] = useState('All');
  const [topicSearch, setTopicSearch] = useState('');
  const [expandedTrackId, setExpandedTrackId] = useState(null);
  const [trackDetail, setTrackDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', topic: '', difficulty: 'Beginner' });
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  // AI generation state
  const [showAiForm, setShowAiForm] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: '', difficulty: 'Beginner', additionalContext: '' });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiConfirming, setAiConfirming] = useState(false);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const loadBrowseTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = [];
      if (topicSearch.trim()) params.push(`topic=${encodeURIComponent(topicSearch.trim())}`);
      if (difficultyFilter !== 'All') params.push(`difficulty=${difficultyFilter}`);
      const query = params.length ? '?' + params.join('&') : '';
      const data = await apiFetchJson(instance, `/api/learningtracks${query}`);
      setTracks(unwrap(data));
    } catch (e) {
      console.error('Failed to load tracks:', e);
      setError('Failed to load learning tracks.');
    } finally {
      setLoading(false);
    }
  }, [instance, topicSearch, difficultyFilter]);

  const loadMyTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetchJson(instance, '/api/learningtracks/my');
      setMyTracks(unwrap(data));
    } catch (e) {
      console.error('Failed to load my tracks:', e);
      setError('Failed to load your tracks.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    if (factFictionMode !== 'fact') return;
    if (view === 'browse') {
      loadBrowseTracks();
    } else {
      loadMyTracks();
    }
  }, [view, factFictionMode, loadBrowseTracks, loadMyTracks]);

  const loadTrackDetail = useCallback(async (trackId) => {
    try {
      setDetailLoading(true);
      const data = await apiFetchJson(instance, `/api/learningtracks/${trackId}`);
      setTrackDetail(data);
    } catch (e) {
      console.error('Failed to load track detail:', e);
      showMessage('Failed to load track details.', 'error');
    } finally {
      setDetailLoading(false);
    }
  }, [instance, showMessage]);

  const handleCardClick = useCallback((trackId) => {
    if (expandedTrackId === trackId) {
      setExpandedTrackId(null);
      setTrackDetail(null);
    } else {
      setExpandedTrackId(trackId);
      loadTrackDetail(trackId);
    }
  }, [expandedTrackId, loadTrackDetail]);

  const handleEnroll = useCallback(async (trackId) => {
    try {
      await apiFetchJson(instance, `/api/learningtracks/${trackId}/enroll`, { method: 'POST' });
      showMessage('Successfully enrolled in track!');
      loadBrowseTracks();
    } catch (e) {
      console.error('Failed to enroll:', e);
      showMessage('Failed to enroll.', 'error');
    }
  }, [instance, showMessage, loadBrowseTracks]);

  const handleAdvanceProgress = useCallback(async (trackId, currentIndex) => {
    try {
      await apiFetchJson(instance, `/api/learningtracks/${trackId}/progress`, {
        method: 'PUT',
        body: JSON.stringify({ currentBookIndex: currentIndex + 1 }),
      });
      showMessage('Progress updated!');
      loadMyTracks();
    } catch (e) {
      console.error('Failed to update progress:', e);
      showMessage('Failed to update progress.', 'error');
    }
  }, [instance, showMessage, loadMyTracks]);

  const handleCreateTrack = useCallback(async (e) => {
    e.preventDefault();
    if (!createForm.title.trim() || !createForm.topic.trim()) return;
    try {
      setCreating(true);
      await apiFetchJson(instance, '/api/learningtracks', {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      showMessage('Track created!');
      setCreateForm({ title: '', description: '', topic: '', difficulty: 'Beginner' });
      setShowCreateForm(false);
      loadBrowseTracks();
    } catch (e) {
      console.error('Failed to create track:', e);
      showMessage('Failed to create track.', 'error');
    } finally {
      setCreating(false);
    }
  }, [instance, createForm, showMessage, loadBrowseTracks]);

  const handleAiGenerate = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!aiForm.topic.trim()) return;
    try {
      setAiGenerating(true);
      setAiPreview(null);
      const res = await apiFetch(instance, '/api/learningtracks/generate', {
        method: 'POST',
        body: JSON.stringify(aiForm),
      });
      if (res.ok) {
        const data = await res.json();
        setAiPreview(data);
      } else if (res.status === 503) {
        showMessage('AI track generation is not configured yet. Please set up Azure OpenAI.', 'error');
      } else {
        showMessage('Failed to generate track. Try again.', 'error');
      }
    } catch (e) {
      console.error('AI generation failed:', e);
      showMessage('Failed to generate track.', 'error');
    } finally {
      setAiGenerating(false);
    }
  }, [instance, aiForm, showMessage]);

  const handleAiConfirm = useCallback(async () => {
    if (!aiPreview) return;
    try {
      setAiConfirming(true);
      await apiFetchJson(instance, '/api/learningtracks/generate/confirm', {
        method: 'POST',
        body: JSON.stringify({
          title: aiPreview.title,
          description: aiPreview.description,
          topic: aiPreview.topic,
          difficulty: aiPreview.difficulty,
          isPublic: true,
          books: aiPreview.books,
        }),
      });
      showMessage('🎉 AI-generated track created!');
      setAiPreview(null);
      setShowAiForm(false);
      setAiForm({ topic: '', difficulty: 'Beginner', additionalContext: '' });
      loadBrowseTracks();
    } catch (e) {
      console.error('AI confirm failed:', e);
      showMessage('Failed to create track.', 'error');
    } finally {
      setAiConfirming(false);
    }
  }, [instance, aiPreview, showMessage, loadBrowseTracks]);

  if (factFictionMode !== 'fact') {
    return (
      <div className="learning-tracks-fiction-notice">
        <span className="learning-tracks-fiction-notice-icon">🛤️</span>
        <p>{t('bookClub.learningTracks.factNotice')}</p>
      </div>
    );
  }

  const progressPercent = (current, total) => total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div className="learning-tracks-container">
      {message && (
        <div className={`learning-tracks-message learning-tracks-message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="learning-tracks-view-toggle">
        <button
          className={`learning-tracks-toggle-btn ${view === 'browse' ? 'active' : ''}`}
          onClick={() => setView('browse')}
        >
          🔍 {t('bookClub.learningTracks.browseTracks')}
        </button>
        <button
          className={`learning-tracks-toggle-btn ${view === 'my' ? 'active' : ''}`}
          onClick={() => setView('my')}
        >
          📋 {t('bookClub.learningTracks.myTracks')}
        </button>
      </div>

      {view === 'browse' && (
        <div className="learning-tracks-browse">
          <div className="learning-tracks-filter-bar">
            <select
              className="learning-tracks-difficulty-select"
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
            >
              {DIFFICULTIES.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <input
              className="learning-tracks-topic-input"
              type="text"
              placeholder={t('bookClub.learningTracks.searchPlaceholder')}
              value={topicSearch}
              onChange={(e) => setTopicSearch(e.target.value)}
            />
          </div>

          <div className="learning-tracks-action-buttons">
            <button
              className="learning-tracks-create-toggle"
              onClick={() => { setShowCreateForm(!showCreateForm); setShowAiForm(false); }}
            >
              {showCreateForm ? `✕ ${t('bookClub.common.cancel')}` : `＋ ${t('bookClub.learningTracks.createTrack')}`}
            </button>
            <button
              className="learning-tracks-ai-toggle"
              onClick={() => { setShowAiForm(!showAiForm); setShowCreateForm(false); setAiPreview(null); }}
            >
              {showAiForm ? `✕ ${t('bookClub.common.cancel')}` : `✨ ${t('bookClub.learningTracks.aiGenerateTrack')}`}
            </button>
          </div>

          {showAiForm && (
            <div className="learning-tracks-ai-section">
              <form className="learning-tracks-ai-form" onSubmit={handleAiGenerate}>
                <div className="learning-tracks-ai-form-header">
                  <span className="learning-tracks-ai-icon">🤖</span>
                  <div>
                    <strong>{t('bookClub.learningTracks.aiGenerator')}</strong>
                    <p className="learning-tracks-ai-subtitle">{t('bookClub.learningTracks.aiPlaceholder')}</p>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Topic (e.g., AI beginner, Norwegian history 1900s, intermediate algebra)"
                  value={aiForm.topic}
                  onChange={(e) => setAiForm(prev => ({ ...prev, topic: e.target.value }))}
                  required
                />
                <select
                  value={aiForm.difficulty}
                  onChange={(e) => setAiForm(prev => ({ ...prev, difficulty: e.target.value }))}
                >
                  {DIFFICULTIES.filter(d => d !== 'All').map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <textarea
                  placeholder="Any preferences? (e.g., 'prefer shorter books', 'interested in practical applications', 'Norwegian language books preferred')"
                  value={aiForm.additionalContext}
                  onChange={(e) => setAiForm(prev => ({ ...prev, additionalContext: e.target.value }))}
                  rows={2}
                />
                <button type="submit" disabled={aiGenerating} className="learning-tracks-ai-submit">
                  {aiGenerating ? `🔄 ${t('bookClub.learningTracks.generating')}` : `✨ ${t('bookClub.learningTracks.generateReadingPath')}`}
                </button>
              </form>

              {aiGenerating && (
                <div className="learning-tracks-ai-loading">
                  <div className="learning-tracks-ai-spinner" />
                  <p>AI is curating your reading path...</p>
                  <p className="learning-tracks-ai-loading-sub">This may take 10-20 seconds</p>
                </div>
              )}

              {aiPreview && (
                <div className="learning-tracks-ai-preview">
                  <div className="learning-tracks-ai-preview-header">
                    <h3>📚 {aiPreview.title}</h3>
                    <span className={`learning-tracks-badge learning-tracks-badge-${DIFFICULTY_COLORS[aiPreview.difficulty] || 'blue'}`}>
                      {aiPreview.difficulty}
                    </span>
                  </div>
                  <p className="learning-tracks-ai-preview-desc">{aiPreview.description}</p>

                  <div className="learning-tracks-ai-book-list">
                    {unwrap(aiPreview.books).sort((a, b) => a.order - b.order).map((book, idx) => (
                      <div key={idx} className={`learning-tracks-ai-book-item ${book.wasFound ? '' : 'not-found'}`}>
                        <div className="learning-tracks-ai-book-number">{idx + 1}</div>
                        <div className="learning-tracks-ai-book-cover-wrap">
                          <BookCover src={book.coverImageUrl} alt={book.title} className="learning-tracks-ai-book-cover" />
                        </div>
                        <div className="learning-tracks-ai-book-info">
                          <strong>{book.title}</strong>
                          <span className="learning-tracks-ai-book-author">by {book.author}</span>
                          <p className="learning-tracks-ai-book-reason">{book.reason}</p>
                          {book.pageCount > 0 && <span className="learning-tracks-ai-book-pages">{book.pageCount} pages</span>}
                          {!book.wasFound && <span className="learning-tracks-ai-book-missing">⚠ Not found in library — will be skipped</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="learning-tracks-ai-preview-actions">
                    <span className="learning-tracks-ai-found-count">
                      {unwrap(aiPreview.books).filter(b => b.wasFound).length} / {unwrap(aiPreview.books).length} {t('bookClub.learningTracks.booksFound')}
                    </span>
                    <button
                      className="learning-tracks-ai-confirm-btn"
                      onClick={handleAiConfirm}
                      disabled={aiConfirming || unwrap(aiPreview.books).filter(b => b.wasFound).length === 0}
                    >
                      {aiConfirming ? t('bookClub.learningTracks.creating') : `✓ ${t('bookClub.learningTracks.createThisTrack')}`}
                    </button>
                    <button
                      className="learning-tracks-ai-regenerate-btn"
                      onClick={handleAiGenerate}
                      disabled={aiGenerating}
                    >
                      🔄 {t('bookClub.learningTracks.regenerate')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showCreateForm && (
            <form className="learning-tracks-create-form" onSubmit={handleCreateTrack}>
              <input
                type="text"
                placeholder={t('bookClub.learningTracks.titlePlaceholder')}
                value={createForm.title}
                onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
              <textarea
                placeholder={t('bookClub.learningTracks.descriptionPlaceholder')}
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
              <input
                type="text"
                placeholder={t('bookClub.learningTracks.topicPlaceholder')}
                value={createForm.topic}
                onChange={(e) => setCreateForm(prev => ({ ...prev, topic: e.target.value }))}
                required
              />
              <select
                value={createForm.difficulty}
                onChange={(e) => setCreateForm(prev => ({ ...prev, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.filter(d => d !== 'All').map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <button type="submit" disabled={creating}>
                {creating ? t('bookClub.learningTracks.creating') : t('bookClub.learningTracks.createTrack')}
              </button>
            </form>
          )}

          {loading && <div className="learning-tracks-loading">{t('bookClub.learningTracks.loading')}</div>}
          {error && <div className="learning-tracks-error">{error}</div>}

          {!loading && !error && tracks.length === 0 && (
            <div className="learning-tracks-empty">{t('bookClub.learningTracks.noBrowseTracks')}</div>
          )}

          <div className="learning-tracks-grid">
            {tracks.map(track => (
              <div key={track.id} className="learning-tracks-card" onClick={() => handleCardClick(track.id)}>
                <div className="learning-tracks-card-header">
                  <h3 className="learning-tracks-card-title">{track.title}</h3>
                  <span className={`learning-tracks-badge learning-tracks-badge-${DIFFICULTY_COLORS[track.difficulty] || 'blue'}`}>
                    {track.difficulty}
                  </span>
                </div>
                <span className="learning-tracks-topic-badge">{track.topic}</span>
                {track.description && (
                  <p className="learning-tracks-card-desc">{track.description}</p>
                )}
                <div className="learning-tracks-card-meta">
                  <span>👥 {track.enrollmentCount} {t('bookClub.recommendations.enrolled')}</span>
                  <span>📚 {track.bookCount} {t('bookClub.common.books')}</span>
                </div>

                {expandedTrackId === track.id && (
                  <div className="learning-tracks-detail" onClick={(e) => e.stopPropagation()}>
                    {detailLoading && <div className="learning-tracks-loading">{t('bookClub.learningTracks.loading')}</div>}
                    {trackDetail && (
                      <>
                        <div className="learning-tracks-book-list">
                          {unwrap(trackDetail.books).map((entry, idx) => (
                            <div key={entry.bookId || idx} className="learning-tracks-book-step">
                              <div className="learning-tracks-step-number">{entry.orderIndex + 1}</div>
                              <div className="learning-tracks-book-info">
                                {entry.book?.coverImageUrl && (
                                  <img
                                    className="learning-tracks-book-cover"
                                    src={entry.book.coverImageUrl}
                                    alt={entry.book.title}
                                  />
                                )}
                                <div>
                                  <strong>{entry.book?.title}</strong>
                                  <span className="learning-tracks-book-author">by {entry.book?.author}</span>
                                  {entry.book?.pageCount > 0 && (
                                    <span className="learning-tracks-book-pages">{entry.book.pageCount} pages</span>
                                  )}
                                  {entry.description && (
                                    <p className="learning-tracks-book-desc">{entry.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {!trackDetail.userProgress && (
                          <button
                            className="learning-tracks-enroll-btn"
                            onClick={() => handleEnroll(track.id)}
                          >
                            {t('bookClub.learningTracks.enrollInTrack')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'my' && (
        <div className="learning-tracks-my">
          {loading && <div className="learning-tracks-loading">{t('bookClub.learningTracks.loading')}</div>}
          {error && <div className="learning-tracks-error">{error}</div>}

          {!loading && !error && myTracks.length === 0 && (
            <div className="learning-tracks-empty">{t('bookClub.learningTracks.noMyTracks')}</div>
          )}

          <div className="learning-tracks-grid">
            {myTracks.map(track => {
              const pct = progressPercent(track.currentBookIndex, track.bookCount);
              const isComplete = track.currentBookIndex >= track.bookCount;
              return (
                <div key={track.id} className="learning-tracks-card learning-tracks-my-card">
                  <div className="learning-tracks-card-header">
                    <h3 className="learning-tracks-card-title">{track.title}</h3>
                    <span className={`learning-tracks-badge learning-tracks-badge-${DIFFICULTY_COLORS[track.difficulty] || 'blue'}`}>
                      {track.difficulty}
                    </span>
                  </div>
                  <span className="learning-tracks-topic-badge">{track.topic}</span>

                  <div className="learning-tracks-progress">
                    <div className="learning-tracks-progress-bar">
                      <div
                        className="learning-tracks-progress-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="learning-tracks-progress-text">
                      {track.currentBookIndex} / {track.bookCount} books ({pct}%)
                    </span>
                  </div>

                  {!isComplete && track.currentBook && (
                    <div className="learning-tracks-current-book">
                      <span className="learning-tracks-current-label">📖 {t('bookClub.learningTracks.continueReading')}</span>
                      <strong>{track.currentBook.title}</strong>
                      <span className="learning-tracks-book-author">by {track.currentBook.author}</span>
                      <button
                        className="learning-tracks-complete-btn"
                        onClick={() => handleAdvanceProgress(track.id, track.currentBookIndex)}
                      >
                        ✓ {t('bookClub.learningTracks.markComplete')}
                      </button>
                    </div>
                  )}

                  {isComplete && (
                    <div className="learning-tracks-completed">
                      🎉 {t('bookClub.learningTracks.trackCompleted')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default LearningTracks;
